import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import Card from "../../common/components/Card";
import CustomForm from '../../common/components/CustomForm';
import DataTable, { customCtrlTypeSupported } from "../../common/components/DataTable";
import DateInput from "../../common/components/DateInput";
import { DefaultButton } from "../../common/components/DefaultButton";
import { FloatingLabel } from "../../common/components/FloatingLabel";
import { Col, Container, Row } from "../../common/components/Grid";
import TextInput from "../../common/components/TextInput";
import TimeInput from "../../common/components/TimeInput";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { now } from "../../common/utils/CDate";
import { useEpcrProcedureDefs } from "../../reducers/epcrProcedureSlice";
import { useEpcrEditorEpcr, useEpcrEditorIntent, useEpcrEditorProcedures } from './EpcrContext';
import PropTypes from 'prop-types';
import { deepClone } from "../../common/utils/objectUtilities";
import TypeaheadInput from "../../common/components/TypeaheadInput";

let genTimestamp = (tz) => {
    let timeNow = now()
    timeNow.seconds(0);
    timeNow.milliseconds(0);
    return timeNow.toISOString();
}

const procOptLabel = proc => proc.name;
const procOptValue = proc => proc.name;
const procOptValueComparator = (proc, idx, v) => proc.name == v;

const EpcrTreatmentProcedures = ({ disabled = false }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const procedureDefs = useEpcrProcedureDefs()
    const { procedures, addProcedure, removeProcedure } = useEpcrEditorProcedures();
    const [newProcedure, setNewProcedure] = useState({ timestamp: genTimestamp(clientConfig.tz) });

    let displayProcedureDefs = useMemo(() => {
        let defs = deepClone(procedureDefs);
        defs.sort();
        return defs;
    }, [procedureDefs])

    let handleAdd = () => {
        addProcedure(newProcedure);
        if (clientConfig.epcrTimestampDefault) {
            setNewProcedure({timestamp: newProcedure.timestamp});
        } else {
            setNewProcedure({});
        }
    }
    let handleRemove = (procedure) => {
        removeProcedure(procedure);
    }

    let addProcedureDisabled = () => {
        return !newProcedure.procedure;
    }
    
    let procedureColumns = [{field: 'timestamp', title: t('timeOfDay'), readonly: true, type: 'time'}, {field: 'procedure', title: t('intervention/procedure'), type: 'text', readonly: true}, {field: 'notes', title: t('results/notes'), type: 'text', readonly: true}]

    return <Card title={t('treatments')}>
        {!disabled ? <>
            {newProcedure.timestamp ? 
            <Row>
                <Col md>
                    <Row>
                        <Col md>
                            <TimeInput value={newProcedure.timestamp} onChange={t => setNewProcedure({...newProcedure, timestamp: t})} placeholder={t('timeOfDay')}></TimeInput>
                        </Col>
                        <Col md>
                            <DateInput value={newProcedure.timestamp} onChange={t => setNewProcedure({...newProcedure, timestamp: t})} placeholder={t('dateTime')}></DateInput>
                        </Col>
                    </Row>
                </Col>
                <Col md="auto">
                    <FloatingLabel>
                        <Button variant='link' onClick={_ => setNewProcedure({...newProcedure, timestamp: undefined})}>{t('clearTimestamp')}</Button>
                    </FloatingLabel>
                </Col>
            </Row>
            :
            <Row>
                <Col md></Col>
                <Col md="auto"><Button variant='link' onClick={_ => setNewProcedure({ ...newProcedure, timestamp: genTimestamp(clientConfig.tz) })}>{t('overrideTimestamp')}</Button></Col>
            </Row>
            }
            <Row className='mb-2'>
                <Col className="d-flex flex-column flex-md-row flex-wrap" md>
                    <div style={{ minWidth: '150px', flex: 1 }}>
                        <TypeaheadInput required placeholder={t('intervention/procedure')} value={newProcedure.procedure} onChange={p => setNewProcedure({...newProcedure, procedure: p.id})} options={displayProcedureDefs} lockdown optLabel={procOptLabel} optValue={procOptValue} optValueComparator={procOptValueComparator}></TypeaheadInput>
                    </div>
                    <div style={{ minWidth: '150px', flex: 1 }}>
                        <TextInput placeholder={t('results/notes')} maxLength={256} value={newProcedure.notes} live={true} onChange={p => setNewProcedure({...newProcedure, notes: p})}></TextInput>
                    </div>
                </Col>
                <Col md="auto">
                    <FloatingLabel placeholder=" "><DefaultButton disabled={addProcedureDisabled()} kind='add' onClick={handleAdd}></DefaultButton></FloatingLabel>
                </Col>
            </Row></>
        :null}
        <DataTable disabled={disabled} defaultSort={['-timestamp']} value={procedures ?? null} columns={procedureColumns} onRemove={handleRemove} emptyMessage={t('noTreatments')}></DataTable>
    </Card>
}

const EpcrTreatment = ({ disabled = false }) => {
    const { epcr, update } = useEpcrEditorEpcr();
    const intent = useEpcrEditorIntent();
    const clientConfig = useClientConfig();

    const runPCRUpdate = useCallback((field, value) => {
        update(field, value);
    }, [update]);

    const def = useMemo(() => {
        if (intent === 'search') {
            let def = deepClone(clientConfig.epcrTreatmentFormSpec);
            def.forEach(section => {
                section.rows = section.rows.map(row => row.filter(cell => customCtrlTypeSupported(cell)));
                section.rows = section.rows.filter(row => row.length > 0);
            });
            def = def.filter(section => section.rows.length > 0);
            return def;
        }
        return clientConfig.epcrTreatmentFormSpec;
    }, [intent, clientConfig.epcrTreatmentFormSpec]);

    return <Container fluid>
        <EpcrTreatmentProcedures disabled={disabled}></EpcrTreatmentProcedures>
        { clientConfig.epcrTreatmentFormSpec && <CustomForm disabled={disabled} data={epcr?.customfields} def={def} hideLabel={true} onChange={c => runPCRUpdate('customfields', c)}></CustomForm> }
    </Container>
}
EpcrTreatment.propTypes = {
    disabled: PropTypes.bool
}

export default EpcrTreatment;